import React, { useEffect, useState } from "react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Mail, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { getSmtpSettings, setSmtpSettings, testSmtpConnection } from "../lib/api_extra";

export default function SmtpSettings() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [pwdEdited, setPwdEdited] = useState(false);

  useEffect(() => {
    getSmtpSettings().then((d) => { setCfg(d.config); setLoading(false); });
  }, []);

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg };
      if (!pwdEdited) delete payload.password;
      const r = await setSmtpSettings(payload);
      setCfg(r.config);
      setPwdEdited(false);
      toast.success("Impostazioni SMTP salvate");
    } catch { toast.error("Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const onTest = async () => {
    setTesting(true);
    try {
      const r = await testSmtpConnection();
      toast.success(r.messaggio);
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Errore connessione SMTP");
    } finally { setTesting(false); }
  };

  if (loading) return <div className="p-8 text-center"><Loader2 size={28} className="animate-spin mx-auto text-[#1B5E20]" /></div>;

  return (
    <div className="space-y-4" data-testid="smtp-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>SMTP & email automatica</h1>
        <p className="text-slate-500 text-sm mt-1">Configurazione invio email mensile (predisposta, da abilitare al deploy)</p>
      </div>
      <Card className="p-5 border-slate-200 space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Mail size={18} className="text-[#1B5E20]" />
          <h2 className="text-base font-semibold">Configurazione SMTP</h2>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Host</Label>
            <Input value={cfg.host} onChange={(e) => setCfg({ ...cfg, host: e.target.value })} placeholder="smtp.gmail.com" data-testid="smtp-host" />
          </div>
          <div>
            <Label>Port</Label>
            <Input type="number" value={cfg.port} onChange={(e) => setCfg({ ...cfg, port: e.target.value })} data-testid="smtp-port" />
          </div>
          <div>
            <Label>Username</Label>
            <Input value={cfg.username} onChange={(e) => setCfg({ ...cfg, username: e.target.value })} data-testid="smtp-username" />
          </div>
          <div>
            <Label>Password</Label>
            <Input type="password" value={cfg.password} onChange={(e) => { setCfg({ ...cfg, password: e.target.value }); setPwdEdited(true); }} placeholder="lascia vuoto per non modificare" data-testid="smtp-password" />
          </div>
          <div>
            <Label>Mittente</Label>
            <Input value={cfg.mittente} onChange={(e) => setCfg({ ...cfg, mittente: e.target.value })} placeholder="noreply@azienda.it" data-testid="smtp-mittente" />
          </div>
          <div>
            <Label>Destinatario report</Label>
            <Input value={cfg.destinatario} onChange={(e) => setCfg({ ...cfg, destinatario: e.target.value })} placeholder="responsabile@azienda.it" data-testid="smtp-destinatario" />
          </div>
        </div>
        <div className="flex items-center justify-between border-t pt-3 mt-2">
          <div className="flex items-center gap-2">
            <Switch checked={cfg.attivo} onCheckedChange={(v) => setCfg({ ...cfg, attivo: v })} data-testid="smtp-attivo" />
            <Label className="text-sm">Attiva invio mensile (1° del mese ore 08:00)</Label>
          </div>
        </div>
        <div className="flex gap-2 pt-2">
          <Button onClick={onSave} disabled={saving} className="bg-[#1B5E20] hover:bg-[#144818] text-white" data-testid="smtp-save">
            {saving ? <Loader2 size={16} className="animate-spin" /> : "Salva"}
          </Button>
          <Button onClick={onTest} disabled={testing} variant="outline" data-testid="smtp-test">
            {testing ? <Loader2 size={16} className="animate-spin mr-1" /> : <Send size={16} className="mr-1" />} Test connessione
          </Button>
        </div>
        <p className="text-[11px] text-slate-400 pt-1">
          ℹ️ La struttura è già predisposta. Il job mensile è schedulato (cron 1° giorno mese 08:00) ma logga "SMTP non configurato" finché host/destinatario sono vuoti o "attivo" è OFF.
        </p>
      </Card>
    </div>
  );
}
